import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Checkbox, Dropdown, Grid, Modal, Loader, Progress } from 'semantic-ui-react';

import Button from '../../../../components/Button';
import { SelectWrapper, ButtonsWrapper, ReportSectionWrapper } from '../../styles';
import { FieldLabel } from '../../../../styles';
import { iRootDispatch, iRootState } from '../../../../store';
import { Props } from './types';
import axiosInstance from '../../../../services/axios-instance';
import {
  dangerNotification,
  successNotification
} from '../../../../services/notification';
import * as Storage from '../../../../services/storage';
import { AvailableBrokersAndEstatesState } from '../../../../store/types/temp-types';

const customLoaderStyle = {
  position: 'relative' as const,
  marginBottom: '20px'
};

const progressBarContainerStyle = {
  width: '100%',
  height: '8px',
  backgroundColor: '#f3f3f3',
  borderRadius: '4px',
  overflow: 'hidden',
  marginTop: '20px',
  marginBottom: '10px'
};

const RealEstateReport: React.FC<Props> = ({
  closeSection,
  availableBrokersAndEstates
}) => {
  const [includeCommissions, setIncludeCommissions] = useState(true);
  const [includeInsurers, setIncludeInsurers] = useState(true);
  const [includeAddress, setIncludeAddress] = useState(true);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [selectedEstatesValues, setSelectedEstatesValues] = useState<string[]>([]);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [reportBlob, setReportBlob] = useState<Blob | null>(null);
  const [progressPercent, setProgressPercent] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Aguarde, processando...');

  const estateOptions =
    availableBrokersAndEstates.estates?.map(option => ({
      text: option.text,
      value: option.value.id as string
    })) || [];

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    if (isExportLoading) {
      setProgressPercent(0);

      interval = setInterval(() => {
        setProgressPercent(prev => {
          const newValue = prev + Math.random() * 1.5;
          return newValue < 90 ? newValue : 89.9;
        });
      }, 800);
    } else if (reportBlob) {
      setProgressPercent(100);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isExportLoading, reportBlob]);

  const onExportButtonClick = async () => {
    setIsExportLoading(true);
    setShowLoadingModal(true);

    try {
      const response = await axiosInstance.get('/estates/export/xlsx', {
        headers: {
          'X-External-Login': Storage.get('external_login')
        },
        params: {
          isRv: true,
          includeInsurers,
          estateId: selectedEstatesValues,
          includeCommissions,
          includeAddress
        },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      setReportBlob(blob);
      setIsExportLoading(false);

      successNotification(
        'Sucesso!',
        'O relatório de imobiliárias foi gerado! Clique em "Baixar agora" para fazer o download.'
      );
    } catch (error) {
      setIsExportLoading(false);
      setShowLoadingModal(false);

      dangerNotification(
        'Oops...',
        'Não foi possível exportar o relatório de imobiliárias, tente novamente mais tarde.'
      );
    }
  };

  const downloadReport = () => {
    if (!reportBlob) return;

    const url = URL.createObjectURL(reportBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Relatório de imobiliárias.xlsx';

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
    setShowLoadingModal(false);
    setReportBlob(null);
  };

  const ProgressBar = ({ percent }: { percent: number }) => {
    return (
      <div style={progressBarContainerStyle}>
        <div
          style={{
            width: `${percent}%`,
            height: '100%',
            backgroundColor: '#59249B',
            borderRadius: '4px',
            transition: 'width 0.5s ease-in-out'
          }}
        />
      </div>
    );
  };

  return (
    <ReportSectionWrapper>
      <Grid.Column width={8}>
        <FieldLabel paddingTop paddingBottom>
          Imobiliária(s)
        </FieldLabel>

        <SelectWrapper>
          <Dropdown
            id="realEstatesSelect"
            placeholder="Todas imobiliárias"
            value={selectedEstatesValues}
            options={estateOptions || []}
            onChange={(_, data) => setSelectedEstatesValues(data.value as string[])}
            style={{ marginBottom: '5px' }}
            menu={{ style: { maxHeight: '300px', overflowY: 'auto' } }}
            noResultsMessage={'Carregando...'}
            clearable
            fluid
            multiple
            search
            selection
          />

          <Checkbox
            id="includeInsurersCheckbox"
            label="Apresentar seguradoras configuradas na imobiliária"
            checked={includeInsurers}
            onClick={() => setIncludeInsurers(state => !state)}
          />

          <Checkbox
            id="includeComissionsCheckbox"
            label="Exibir valores de comissionamento por seguradora"
            checked={includeCommissions}
            onClick={() => setIncludeCommissions(state => !state)}
          />

          <Checkbox
            id="includeAddressCheckbox"
            label="Apresentar os endereços das imobiliárias"
            checked={includeAddress}
            onClick={() => setIncludeAddress(state => !state)}
          />
        </SelectWrapper>
      </Grid.Column>

      <Grid.Column computer={8} tablet={8} mobile={16}>
        <ButtonsWrapper>
          <Button
            type="button"
            text="Cancelar"
            disabled={isExportLoading}
            onClick={closeSection}
            light
          />

          <Button
            type="button"
            text="Gerar relatório"
            disabled={isExportLoading}
            onClick={() => onExportButtonClick()}
          />
        </ButtonsWrapper>
      </Grid.Column>

      <Modal
        open={showLoadingModal}
        size="tiny"
        closeOnDimmerClick={false}
        closeOnDocumentClick={false}
        style={{ textAlign: 'center' }}
      >
        <Modal.Header>
          {isExportLoading ? 'Gerando relatório...' : 'Relatório gerado com sucesso!'}
        </Modal.Header>
        <Modal.Content>
          {isExportLoading ? (
            <div
              style={{
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                color: '#59249B'
              }}
            >
              <div style={customLoaderStyle}>
                <Loader
                  active
                  inline
                  size="large"
                  style={{
                    filter: 'invert(1)'
                  }}
                />
              </div>

              <ProgressBar percent={progressPercent} />

              <p
                style={{
                  textAlign: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#333',
                  marginBottom: '5px',
                  marginTop: '10px'
                }}
              >
                {loadingMessage}
              </p>

              <p style={{ marginTop: '10px', fontSize: '14px' }}>
                Este processo pode levar alguns minutos.
                <br />
                <span
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    marginTop: '5px',
                    display: 'block'
                  }}
                >
                  Por favor, não feche esta janela enquanto o relatório está sendo gerado.
                </span>
              </p>
            </div>
          ) : (
            <div style={{ padding: '10px' }}>
              <ProgressBar percent={100} />
              <p>Seu relatório está pronto para download!</p>
              <div
                style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}
              >
                <Button type="button" text="Baixar agora" onClick={downloadReport} />
                <Button
                  type="button"
                  text="Fechar"
                  onClick={() => {
                    setShowLoadingModal(false);
                    setReportBlob(null);
                  }}
                  light
                  style={{ marginLeft: '10px' }}
                />
              </div>
            </div>
          )}
        </Modal.Content>
      </Modal>
    </ReportSectionWrapper>
  );
};

const mapStateToProps = (state: iRootState) => ({
  user: state.user,
  availableBrokersAndEstates: state.availableBrokersAndEstates
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(availableBrokers)
});

export default connect(mapStateToProps, mapDispatch)(RealEstateReport);
